import { WebAuthnSigner } from "@dfns/sdk-browser";
import Parse from "parse";

/** WebAuthn rpId must equal the current host or be a registrable domain suffix of it. */
function resolveDfnsRelyingPartyId(): string {
  const envId = process.env.REACT_APP_DFNS_RELYING_PARTY?.trim();
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname;
    if (!envId || envId === "localhost") {
      return host;
    }
    if (host === envId || host.endsWith(`.${envId}`)) {
      return envId;
    }
    console.warn(`[CreateCredential] REACT_APP_DFNS_RELYING_PARTY (${envId}) does not match hostname (${host}); using hostname.`);
    return host;
  }
  return envId || "localhost";
}

// Created once per module load (same as the previous singleton class).
const webauthn = new WebAuthnSigner({
  relyingParty: {
    id: resolveDfnsRelyingPartyId(),
    name: "Nomyx Admin Portal",
  },
});

async function initiateCreateCredentialCode(expiration: string | number, dfns_token: string) {
  if (expiration == null || dfns_token == null) {
    throw new Error("Missing required parameters for create credential code.");
  }

  try {
    const initiateResponse = await Parse.Cloud.run("createCredentialCodeInit", {
      expiration,
      dfns_token,
    });
    return { initiateResponse, error: null };
  } catch (error: any) {
    console.error("Error initiating create credential code:", error);
    return { initiateResponse: null, error: error.message };
  }
}

async function completeCreateCredentialCode(dfns_token: string, challenge: any, requestBody: any) {
  if (!dfns_token || !challenge || !requestBody) {
    throw new Error("Missing required parameters for completing create credential code.");
  }

  try {
    const assertion = await webauthn.sign(challenge);

    const completeResponse = await Parse.Cloud.run("createCredentialCodeComplete", {
      dfns_token,
      signedChallenge: {
        challengeIdentifier: challenge.challengeIdentifier,
        firstFactor: assertion,
      },
      requestBody,
    });
    return { completeResponse, error: null };
  } catch (error: any) {
    console.error("Error completing create credential code:", error);
    return { completeResponse: null, error: error.message };
  }
}

async function initiateCreateCredentialWithCode(
  credentialKind: "Fido2" | "Key" | "Password" | "Totp" | "RecoveryKey" | "PasswordProtectedKey",
  code: string
) {
  if (!code || !credentialKind) {
    throw new Error("Missing required parameters for initiating credential with code.");
  }

  try {
    const initiateResponse = await Parse.Cloud.run("createCredentialChallengeWithCode", {
      code,
      credentialKind,
    });
    return { initiateResponse, error: null };
  } catch (error: any) {
    console.error("Error initiating createCredentialWithCode:", error);
    return { initiateResponse: null, error: error.message };
  }
}

async function completeCreateCredentialWithCode(
  credentialKind: "Fido2" | "Key" | "Password" | "Totp" | "RecoveryKey" | "PasswordProtectedKey",
  challenge: any,
  credentialName: string,
  challengeIdentifier: string,
  extraParams?: Partial<{ encryptedPrivateKey: string }>
) {
  if (!credentialKind || !credentialName || !challengeIdentifier) {
    throw new Error("Missing required parameters for completing credential with code.");
  }

  try {
    const webauthnResult = await webauthn.create(challenge);
    const credentialInfo = (webauthnResult as any).credentialInfo || webauthnResult;

    const completeBody: any = {
      credentialKind,
      credentialInfo,
      credentialName,
      challengeIdentifier,
      ...(extraParams || {}),
    };

    const completeResponse = await Parse.Cloud.run("createCredentialWithCode", {
      body: completeBody,
    });

    return { completeResponse, error: null };
  } catch (error: any) {
    console.error("Error completing createCredentialWithCode:", error);
    return { completeResponse: null, error: error.message };
  }
}

const CreateCredentialService = {
  initiateCreateCredentialCode,
  completeCreateCredentialCode,
  initiateCreateCredentialWithCode,
  completeCreateCredentialWithCode,
};

export default CreateCredentialService;
